import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ChatComposer from '../Onboarding/chat/ChatComposer';
import ChatArtifactActionsMenu from './ChatArtifactActionsMenu';
import useChatVoiceInput from './useChatVoiceInput';
import { CHAT_V2_INPUT_PLACEHOLDER, CHAT_V2_SUGGESTED_PROMPTS } from './chatV2Constants';
import { isMockChatSessionsEnabled, MOCK_CHAT_VOICE_FALLBACK } from '../../fixture/mockChatSessions';
import useMobileKeyboardOffset from '../../hooks/useMobileKeyboardOffset';

const promptText = (suggestion) => {
  if (typeof suggestion === 'string') return suggestion;
  return suggestion?.prompt || suggestion?.label || '';
};

/**
 * Product chat composer — reuses onboarding ChatComposer styling and behavior.
 */
export default function ProductChatComposer({
  onSend,
  disabled = false,
  layout = 'docked',
  placeholder = CHAT_V2_INPUT_PLACEHOLDER,
  suggestions = [],
  showSuggestions = false,
  composerActions = [],
  onRunComposerAction,
  actionInFlightKey = null,
  prefill = null,
  submitSuggestionOnClick = true,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const keyboardOffset = useMobileKeyboardOffset();
  const { draft, setDraft, listening, handleToggleVoice } = useChatVoiceInput({
    fallbackTranscript: isMockChatSessionsEnabled() ? MOCK_CHAT_VOICE_FALLBACK : '',
  });

  useEffect(() => {
    if (!prefill || typeof prefill.text !== 'string') return;
    setDraft(prefill.text);
  }, [prefill, setDraft]);

  const handleSubmit = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || disabled) return;
    onSend?.(trimmed);
    setDraft('');
  };

  const handleToggleSuggestion = (suggestion) => {
    const text = promptText(suggestion);
    if (!text) return;
    if (submitSuggestionOnClick) {
      handleSubmit(text);
      return;
    }
    setDraft(text);
  };

  const composer = (
    <ChatComposer
      placeholder={placeholder}
      suggestions={showSuggestions ? suggestions : []}
      selection="text"
      pendingSelection={[]}
      onToggleSuggestion={handleToggleSuggestion}
      onSubmit={handleSubmit}
      disabled={disabled}
      listening={listening}
      onToggleVoice={handleToggleVoice}
      draftValue={draft}
      onDraftChange={setDraft}
      layout={layout}
    />
  );

  const mobileDockedBottomPad = isMobile && keyboardOffset > 0
    ? 0
    : undefined;
  const mobileDockedMarginBottom = isMobile && keyboardOffset > 0
    ? `${keyboardOffset}px`
    : undefined;

  const shellSx = layout === 'hero'
    ? { width: '100%', maxWidth: 580, mx: 'auto' }
    : {
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
        px: { xs: 2, sm: 2.5 },
        pt: 1.25,
        pb: mobileDockedBottomPad ?? { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 1.5 },
        ...(mobileDockedMarginBottom ? { mb: mobileDockedMarginBottom } : {}),
        background: isDark
          ? theme.palette.background.default
          : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 22%)',
      };

  if (layout === 'docked' && composerActions.length > 0) {
    return (
      <Box data-testid="product-chat-composer" data-layout={layout} sx={shellSx}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75 }}>
          <ChatArtifactActionsMenu
            actions={composerActions}
            actionInFlightKey={actionInFlightKey}
            onRunAction={onRunComposerAction}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>{composer}</Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box data-testid="product-chat-composer" data-layout={layout} sx={shellSx}>
      {composer}
    </Box>
  );
}

ProductChatComposer.defaultProps = {
  suggestions: CHAT_V2_SUGGESTED_PROMPTS,
};
